import { useQuery } from '@tanstack/vue-query';
import { useExperienceApi } from '@/shared/api/useExperienceApi';
import { transformExperienceSelections } from '@/entities/expSelection/lib/transformExperienceSelections';
import { useDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayCurrencyQueryParams.ts';
import { type ComputedRef, type MaybeRefOrGetter, unref } from 'vue';

export interface ExpSelectionsParams {
  page?: MaybeRefOrGetter<number>;
  bookable?: MaybeRefOrGetter<string>;
  type?: MaybeRefOrGetter<string>;
  city__slug?: MaybeRefOrGetter<string>;
  price_format?: MaybeRefOrGetter<string>;
  region__slug?: MaybeRefOrGetter<string>;
  country__slug?: MaybeRefOrGetter<string>;
  tag__slugs?: MaybeRefOrGetter<string>;
  start_price?: MaybeRefOrGetter<number | null>;
  end_price?: MaybeRefOrGetter<number | null>;
  start_duration?: MaybeRefOrGetter<number | null>;
  end_duration?: MaybeRefOrGetter<number | null>;
  start_date?: MaybeRefOrGetter<string | null>;
  end_date?: MaybeRefOrGetter<string | null>;
  movement_type?: MaybeRefOrGetter<string | null>;
  persons_count?: MaybeRefOrGetter<number>;
  start_time_interval?: MaybeRefOrGetter<string>;
  comfort_type?: MaybeRefOrGetter<string>;
  activity_level_category?: MaybeRefOrGetter<string>;
  accommodation_type?: MaybeRefOrGetter<string>;
  duration_interval?: MaybeRefOrGetter<string>;
  children_allowed?: MaybeRefOrGetter<boolean>;
  experiment_related_tags?: MaybeRefOrGetter<boolean>;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const normalizeQueryParams = (params: ExpSelectionsParams) => {
  return {
    city__slug: params?.city__slug ? (unref(params?.city__slug) as string) : undefined,
    region__slug: params?.region__slug ? (unref(params?.region__slug) as string) : undefined,
    country__slug: params?.country__slug ? (unref(params?.country__slug) as string) : undefined,
    type: params?.type ? (unref(params?.type) as string) : undefined,
    start_date: params?.start_date ? (unref(params?.start_date) as string) : undefined,
    end_date: params?.end_date ? (unref(params?.end_date) as string) : undefined,
    movement_type: params?.movement_type ? (unref(params?.movement_type) as string) : undefined,
    start_price: params?.start_price ? (unref(params?.start_price) as number) : undefined,
    end_price: params?.end_price ? (unref(params?.end_price) as number) : undefined,
    start_duration: params?.start_duration ? (unref(params?.start_duration) as number) : undefined,
    end_duration: params?.end_duration ? (unref(params?.end_duration) as number) : undefined,
    persons_count: params?.persons_count ? (unref(params?.persons_count) as number) : undefined,
    page: params?.page ? (unref(params?.page) as number) : undefined,
    tag__slugs: params?.tag__slugs ? (unref(params?.tag__slugs) as string) : undefined,
    bookable: params?.bookable === 'true' ? (unref(Boolean(params?.bookable)) as boolean) : undefined,
    start_time_interval: params?.start_time_interval ? (unref(params?.start_time_interval) as string) : undefined,
    comfort_type: params?.comfort_type ? (unref(params?.comfort_type) as string) : undefined,
    accommodation_type: params?.accommodation_type ? (unref(params?.accommodation_type) as string) : undefined,
    activity_level_category: params?.activity_level_category
      ? (unref(params?.activity_level_category) as string)
      : undefined,
    duration_interval: params?.duration_interval ? (unref(params?.duration_interval) as string) : undefined,
    price_format: params?.price_format ? (unref(params?.price_format) as string) : undefined,
    children_allowed: params?.children_allowed !== undefined ? (unref(params?.children_allowed) as boolean) : undefined,
    experiment_related_tags:
      params?.experiment_related_tags !== undefined ? (unref(params?.experiment_related_tags) as boolean) : undefined,
  };
};

/**
 * Загружает подборки экскурсий только когда вызывающий блок реально должен их показать.
 */
export const useExpSelections = (
  params: ComputedRef<ExpSelectionsParams>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  const { getRelatedExperiences } = useExperienceApi();
  const displayCurrencyQueryParams = useDisplayCurrencyQueryParams();

  const selectionsQueryKeyParams = computed(() => ({
    ...normalizeQueryParams(params.value),
    ...displayCurrencyQueryParams.value,
  }));

  const {
    data: selections,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: computed(() => ['experienceSelections', selectionsQueryKeyParams.value]),
    queryFn: async () => {
      const response = await getRelatedExperiences({
        query: {
          ...selectionsQueryKeyParams.value,
          photos_count: 1,
        },
      });

      return transformExperienceSelections(response?.data ?? []);
    },
    placeholderData: (prevData, previousQuery) => {
      const prevParams = previousQuery?.queryKey?.[1] as { display_currency?: string } | undefined;
      const nextCurrency = selectionsQueryKeyParams.value.display_currency;

      if (prevParams?.display_currency !== nextCurrency) {
        return undefined;
      }

      return prevData;
    },
    retry: false,
    enabled,
  });

  return {
    isFetched,
    selections,
    refetch,
  };
};
